import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../config/supabase';
import EventCard from '../components/EventCard';
import LoadingSpinner from '../components/LoadingSpinner';
import LoginRequired from '../components/LoginRequired';
import { getBulkEventStats7Days } from '../utils/statsUtils';
import { getEventsByIds } from '../services/eventService';
import { HeartIcon } from '../components/icons/NavIcons';
import { EventIcon } from '../components/icons/NavIcons';
import { EditIcon, HourglassIcon, PublishedIcon, MessageIcon } from '../components/icons/NavIcons';

/**
 * Page Mes événements
 * Affiche les favoris de l'utilisateur et les événements créés
 */
const MyEventsPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => {
    // Restaurer l'onglet depuis le localStorage au chargement initial
    return localStorage.getItem('myEventsActiveTab') || 'favorites';
  });
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [favoriteEvents, setFavoriteEvents] = useState([]);
  const [myEvents, setMyEvents] = useState([]);
  const [eventStats, setEventStats] = useState({});

  // Récupérer l'utilisateur connecté ET ses données en une seule fois
  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      
      try {
        // 1. Récupérer l'utilisateur
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        
        if (userError) {
          // Si l'erreur est "Auth session missing", c'est normal (utilisateur non connecté)
          if (userError.message !== 'Auth session missing!') {
            console.error("Erreur lors de la récupération de l'utilisateur:", userError);
          }
          setCurrentUser(null);
          setFavoriteEvents([]);
          setMyEvents([]);
          setLoading(false);
          return;
        }
        
        if (!user) {
          setCurrentUser(null);
          setFavoriteEvents([]);
          setMyEvents([]);
          setLoading(false);
          return;
        }
        
        setCurrentUser(user);

        // 2. Récupérer le profil pour le rôle
        const { data: roleData, error: roleError } = await supabase
          .from('roles')
          .select('role')
          .eq('id', user.id)
          .maybeSingle();

        if (roleError) {
          console.error('[MyEventsPage] Erreur lors de la récupération du rôle:', roleError);
        }

        const isAdmin = roleData?.role === 'admin';

        // 3. Récupérer les événements de l'utilisateur
        let eventsQuery = supabase
          .from('events')
          .select(`
            *,
            category:category_id (name, icon, color_code),
            age_group:age_group_id (name, icon)
          `)
          .order('start_date', { ascending: false });

        if (isAdmin) {
          eventsQuery = eventsQuery.or(`organizer_id.eq.${user.id},organizer_id.is.null`);
        } else {
          eventsQuery = eventsQuery.eq('organizer_id', user.id);
        }

        const { data: eventsData, error: eventsError } = await eventsQuery;
        if (eventsError) throw eventsError;

        // 4. Récupérer les signalements
        const eventIds = eventsData?.map(e => e.id) || [];
        let reportsData = [];
        
        if (eventIds.length > 0) {
          const { data: reports, error: reportsError } = await supabase
            .from('reports')
            .select('event_id')
            .in('event_id', eventIds)
            .eq('status', 'pending');

          if (!reportsError && reports) {
            reportsData = reports;
          }
        }

        const reportCounts = {};
        reportsData.forEach(report => {
          reportCounts[report.event_id] = (reportCounts[report.event_id] || 0) + 1;
        });

        const eventsWithReports = eventsData?.map(event => ({
          ...event,
          pending_reports_count: reportCounts[event.id] || 0
        })) || [];

        setMyEvents(eventsWithReports);

        // 5. Récupérer les favoris
        const { data: favData, error: favError } = await supabase
          .from('favorites')
          .select('event_id')
          .eq('user_id', user.id);

        if (favError) throw favError;

        const favEventIds = favData?.map(fav => fav.event_id) || [];
        
        let favEventsData = [];
        if (favEventIds.length > 0) {
          // Utilisation de la Edge Function get-event-by-id pour récupérer les événements favoris
          try {
            console.log(`[MyEventsPage] Récupération de ${favEventIds.length} événements favoris via Edge Function`);
            favEventsData = await getEventsByIds(favEventIds);
            setFavoriteEvents(favEventsData);
            console.log(`[MyEventsPage] ${favEventsData.length} événements favoris récupérés`);
          } catch (eventError) {
            console.error('[MyEventsPage] Erreur lors de la récupération des événements favoris:', eventError);
            // Fallback : récupération directe si la Edge Function échoue
            console.log('[MyEventsPage] Fallback : récupération directe depuis Supabase');
            const { data: fetchedFavEvents, error: favEventsError } = await supabase
              .from('events')
              .select(`
                *,
                category:category_id (name, icon, color_code),
                age_group:age_group_id (name, icon)
              `)
              .in('id', favEventIds);

            if (favEventsError) throw favEventsError;
            favEventsData = fetchedFavEvents || [];
            setFavoriteEvents(favEventsData);
          }
        } else {
          setFavoriteEvents([]);
        }

        // 6. Récupérer les statistiques pour tous les événements de l'utilisateur
        const allUserEventIds = eventsWithReports.map(e => e.id);
        const favoriteEventIds = favEventsData.map(e => e.id);
        const allEventIds = [...new Set([...allUserEventIds, ...favoriteEventIds])];

        if (allEventIds.length > 0) {
          console.log('[Stats] Récupération des statistiques pour', allEventIds.length, 'événements');
          const stats = await getBulkEventStats7Days(allEventIds);
          setEventStats(stats);
          console.log('[Stats] Statistiques récupérées:', stats);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        setFavoriteEvents([]);
        setMyEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  // Activer l'onglet selon le state de navigation
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
      localStorage.setItem('myEventsActiveTab', location.state.activeTab);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Sauvegarder l'onglet actif dans le localStorage à chaque changement
  useEffect(() => {
    localStorage.setItem('myEventsActiveTab', activeTab);
  }, [activeTab]);

  // // Réinitialiser le scroll au chargement initial et lors du changement d'onglet
  // useEffect(() => {
  //   window.scrollTo(0, 0);
  //   document.documentElement.scrollTop = 0;
  //   document.body.scrollTop = 0;
  // }, [activeTab]);

  // Grouper les favoris par date avec la même logique qu'EventListPage
  const groupedFavorites = useMemo(() => {
    const groups = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    favoriteEvents.forEach(event => {
      if (!event.start_date) return;
      const startDate = new Date(event.start_date);
      const endDate = event.end_date ? new Date(event.end_date) : null;

      let key;
      let isToday = false;
      let sortDate = startDate;
      let isOngoing = false;
      
      // Calculer isPast basé sur end_date si disponible, sinon start_date
      const eventDate = endDate || startDate;
      const isPast = eventDate < today;
      
      // Si l'événement a déjà commencé (date de début dans le passé)
      if (startDate < today && endDate && endDate >= today) {
        // Afficher "Jusqu'au" + date de fin
        const formattedEndDate = endDate.toLocaleDateString(i18n.language, { weekday: 'long', day: 'numeric', month: 'long' });
        key = `${t('eventList.until')} ${formattedEndDate}`;
        sortDate = endDate;
        isOngoing = true;
      } else if (startDate.toDateString() === today.toDateString()) {
        key = t('common.today');
        isToday = true;
      } else {
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        if (startDate.toDateString() === tomorrow.toDateString()) {
          key = t('common.tomorrow');
        } else {
          key = startDate.toLocaleDateString(i18n.language, { weekday: 'long', day: 'numeric', month: 'long' });
        }
      }
      
      if (!groups[key]) {
        groups[key] = { events: [], isToday, date: sortDate, isOngoing };
      }
      
      // Ajouter isCreator et isPast à l'événement
      const isCreator = currentUser && event.organizer_id === currentUser.id;
      console.log(`📊 [Favorite ${event.id}] organizer_id: ${event.organizer_id}, currentUser: ${currentUser?.id}, isCreator: ${isCreator}, isPast: ${isPast}`);
      
      groups[key].events.push({
        ...event,
        isCreator,
        isPast
      });
    });
    return groups;
  }, [favoriteEvents, t, i18n.language, currentUser]);

  // Trier les groupes par date (ordre chronologique comme EventListPage)
  const sortedGroups = useMemo(() => {
    return Object.entries(groupedFavorites).sort((a, b) => {
      return a[1].date - b[1].date;
    });
  }, [groupedFavorites]);
  
  // Gestion du statut d'événement
  const getStatusBadge = (event) => {
    switch (event.status) {
      case 'draft':
        return <span className="bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full text-xs">📝 {t('eventStatus.draft')}</span>;
      case 'pending_approval':
        return <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs">⏳ {t('eventStatus.pendingApproval')}</span>;
      case 'published':
        return event.pending_reports_count > 0 ? (
          <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded-full text-xs font-bold">
            ⚠️ {event.pending_reports_count} {event.pending_reports_count > 1 ? t('settings.admin.reports.reports') : t('settings.admin.reports.report')}
          </span>
        ) : (
          <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-xs">✅ {t('eventStatus.published')}</span>
        );
      case 'banned':
        return <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded-full text-xs">🚫 {t('eventStatus.banned')}</span>;
      default:
        return null;
    }
  };

  // Grouper les événements créés par statut
  const groupedMyEvents = useMemo(() => {
    const groups = {
      toReview: [],
      pending: [],
      published: []
    };

    myEvents.forEach(event => {
      if (event.status === 'draft') {
        groups.toReview.push(event);
      } else if (event.status === 'pending_approval') {
        groups.pending.push(event);
      } else if (event.status === 'published' || event.status === 'banned') {
        groups.published.push(event);
      }
    });

    return groups;
  }, [myEvents]);
  
  // Composant pour afficher un événement créé (avec statut séparé)
  const MyEventCard = ({ event }) => {
    const handleClick = () => {
      if (event.status === 'draft') {
        navigate(`/edit-event/${event.id}`);
      } else {
        navigate(`/event/${event.id}`);
      }
    };

    // Calculer isPast (basé sur end_date si disponible, sinon start_date)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = event.end_date ? new Date(event.end_date) : null;
    const startDate = new Date(event.start_date);
    const eventDate = endDate || startDate;
    const isPast = eventDate < today;

    // console.log(`📊 [MyEventCard ${event.id}] organizer_id: ${event.organizer_id}, currentUser: ${currentUser?.id}, color_code: ${event.category?.color_code}`);

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Message du modérateur si présent */}
        {event.status === 'draft' && event.message && (
          <div className="bg-orange-50 border-b border-orange-200 p-3">
            <div className="flex items-start">
              <MessageIcon className="w-5 h-5 mr-2 text-orange-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs font-medium text-orange-900 mb-1">{t('editEvent.moderatorMessage')}</p>
                <p className="text-sm text-orange-800">{event.message}</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Contenu de l'événement */}
        <div className="p-3 space-y-2">
          {/* Date de création */}
          {event.created_at && (
            <div className="text-xs text-gray-500">
              {t('myEvents.createdOn')} {new Date(event.created_at).toLocaleDateString(i18n.language, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          )}
          
          {/* Tuile d'événement */}
          <EventCard
            event={event}
            onClick={handleClick}
            isPast={isPast}
            isFavorite={false}
            isCreator={true}
            stats={eventStats[event.id] || []}
            categoryColorCode={event.category?.color_code}
          />
        </div>
      </div>
    );
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="">
      {/* Onglets */}
      <div className="fixed top-0 left-0 right-0 z-[999] bg-white border-b border-gray-200 shadow-sm pt-4 px-4 pb-4">
        <div className="flex border-b">
          <button
            className={`flex-1 py-2 ${activeTab === 'favorites' ? 'border-b-2 border-[#2894D8] text-[#2894D8] font-medium' : 'text-gray-500'}`}
            onClick={() => setActiveTab('favorites')}
          >
            {t('myEvents.tabs.favorites')}
          </button>
          <button
            className={`flex-1 py-2 ${activeTab === 'myEvents' ? 'border-b-2 border-[#2894D8] text-[#2894D8] font-medium' : 'text-gray-500'}`}
            onClick={() => setActiveTab('myEvents')}
          >
            {t('myEvents.tabs.myEvents')}
          </button>
        </div>
      </div>
      
      {/* Contenu des onglets */}
      <div className="h-20 shrink-0" />
      <div className="pb-20">
        {/* Onglet Favoris */}
        {activeTab === 'favorites' && (
          <>
            {!currentUser ? (
              <div className="max-w-2xl mx-auto flex flex-col items-center justify-center h-[calc(100vh-180px)] p-4">
              <LoginRequired />
              </div>
            ) : favoriteEvents.length === 0 ? (
              <div className="max-w-2xl mx-auto flex flex-col items-center justify-center h-[calc(100vh-180px)] p-4">
                <div className="flex flex-col items-center justify-center py-1">
                  <HeartIcon className="w-16 h-16 mb-4 text-bleu1" />
                </div>
                <h2 className="text-h1 text-center mb-4">{t('myEvents.noFavorites')}</h2>
                
              </div>
            ) : (
              <div className="px-4">
                {sortedGroups.map(([date, { events: eventsForDate, isToday }]) => (
                  <section key={date} className="mb-6">
                    <div className={`relative ${isToday ? 'pt-1' : ''}`}>
                      {/* Trait rouge horizontal pour la date du jour */}
                      {isToday && (
                        <div className="absolute -top-1 left-0 right-0 h-0.5 bg-red-500 shadow-sm"></div>
                      )}
                      <h2 className={`text-lg font-medium mb-2 mt-2 ${isToday ? 'text-red-600 font-bold' : ''}`}>
                        {date}
                      </h2>
                    </div>
                    <div className="space-y-4">
                      {eventsForDate.map(event => (
                        <EventCard
                          key={event.id}
                          event={event}
                          onClick={() => navigate(`/event/${event.id}`)}
                          isPast={event.isPast}
                          isFavorite={true}
                          isCreator={event.isCreator}
                          stats={event.isCreator ? (eventStats[event.id] || []) : []}
                          categoryColorCode={event.category?.color_code}
                        />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </>
        )}
        
        {/* Onglet Mes événements */}
        {activeTab === 'myEvents' && (
          <>
            {!currentUser ? (
              <div className="max-w-2xl mx-auto flex flex-col items-center justify-center h-[calc(100vh-180px)] p-4">
              <LoginRequired />
              </div>
            ) : myEvents.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-[calc(100vh-180px)] p-4">
                <div className="mb-6">
                  <EventIcon className="w-16 h-16 text-bleu1" />
                </div>
                <h2 className="text-h1 text-center mb-4">{t('myEvents.noEvents')}</h2>
                                
                <button 
                  onClick={() => navigate('/create-event')}
                  className="bg-[#2894D8] text-white py-3 px-6 rounded-lg font-medium hover:bg-[#136E9D] transition-colors"
                >
                  {t('myEvents.createEvent')}
                </button>
              </div>
            ) : (
              <div className="p-4 space-y-6">
                {/* Section 1: Événements à revoir */}
                {groupedMyEvents.toReview.length > 0 && (
                  <section>
                    <div className="flex items-center gap-2 mb-3">
                      <EditIcon className="w-6 h-6 text-orange-600" />
                      <h2 className="text-h2 text-orange-600">
                        Événements à revoir
                      </h2>
                      <span className="bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full text-xs font-medium">
                        {groupedMyEvents.toReview.length}
                      </span>
                    </div>
                    <div className="space-y-4">
                      {groupedMyEvents.toReview.map(event => (
                        <MyEventCard key={event.id} event={event} />
                      ))}
                    </div>
                  </section>
                )}

                {/* Section 2: Événements en cours d'approbation */}
                {groupedMyEvents.pending.length > 0 && (
                  <section>
                    <div className="flex items-center gap-2 mb-3">
                      <HourglassIcon className="w-6 h-6 text-blue-600" />
                      <h2 className="text-h2 text-blue-600">
                        {t('eventStatus.pendingApproval')}
                      </h2>
                      <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs font-medium">
                        {groupedMyEvents.pending.length}
                      </span>
                    </div>
                    <div className="space-y-4">
                      {groupedMyEvents.pending.map(event => (
                        <MyEventCard key={event.id} event={event} />
                      ))}
                    </div>
                  </section>
                )}

                {/* Section 3: Événements publiés */}
                {groupedMyEvents.published.length > 0 && (
                  <section>
                    <div className="flex items-center gap-2 mb-3">
                      <PublishedIcon className="w-6 h-6 text-green-600" />
                      <h2 className="text-h2 text-green-600">
                        {t('eventStatus.published')}
                      </h2>
                      <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-xs font-medium">
                        {groupedMyEvents.published.length}
                      </span>
                    </div>
                    <div className="space-y-4">
                      {groupedMyEvents.published.map(event => (
                        <MyEventCard key={event.id} event={event} />
                      ))}
                    </div>
                  </section>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MyEventsPage;
